import { equals } from "ramda";
import type { StoreApi } from "zustand";

import { PileupRenderingModeEnum } from "../../openapi";
import type { GenomeViewerStore } from "../../store/store.ts";
import { mergeOrthologs } from "../../synteny-worker/utils/merge-orthologs.ts";
import {
  type MainThreadHighlightMessage,
  type MainThreadHoverMessage,
  type MainThreadImageBitmapMessage,
  type TrackWorkerMessageType,
  type WorkerMessage,
} from "../../types.ts";
import { alignActiveFamily } from "../../utils/align.ts";
import { FetchError } from "../../utils/errors.ts";
import { getTrackTypeBySyntenyType } from "../../utils/type-conversion.ts";
import { refreshMain } from "../render-main.ts";
import { getAllTracksHeight } from "../utils.ts";
import { refreshTracks } from "./post-track-messages.ts";

// Message type constants to avoid string comparisons
const MESSAGE_TYPES = {
  IMAGE_BITMAP: "imageBitmap",
  ORTHOLOG: "ortholog",
  ERROR: "error",
  HOVER: "hover",
  HIGHLIGHT: "highlight",
} as const;

// Create a Map for message handlers to avoid switch statement
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
const messageHandlers = new Map<TrackWorkerMessageType, Function>([
  [MESSAGE_TYPES.IMAGE_BITMAP, handleImageBitmap],
  [MESSAGE_TYPES.ORTHOLOG, handleOrtholog],
  [MESSAGE_TYPES.HOVER, handleHover],
  [MESSAGE_TYPES.HIGHLIGHT, handleHighlight],
  [MESSAGE_TYPES.ERROR, handleError],
]);

// Worker Management
export function setupWorkerMessageHandler(
  worker: Worker,
  set: StoreApi<GenomeViewerStore>["setState"],
  get: StoreApi<GenomeViewerStore>["getState"],
  onError: (error: Error | FetchError) => void,
) {
  worker.onmessage = function (e) {
    const handler = messageHandlers.get(e.data?.type);
    if (handler) {
      const { type: _, ...rest } = e.data;
      handler(rest, get, set, onError);
    }
  };
}

// Individual message handlers
function handleImageBitmap(
  data: MainThreadImageBitmapMessage,
  get: StoreApi<GenomeViewerStore>["getState"],
  set: StoreApi<GenomeViewerStore>["setState"],
) {
  const { trackId, imageBitmap, baseStart, basePerPixel } = data;
  // We modify tracks in place, no need to trigger a render.
  const tracks = get().tracks;
  const track = tracks.get(trackId);

  if (track != undefined) {
    if (track.imageBitmap) {
      track.imageBitmap.close();
    }

    track.imageBitmap = imageBitmap;
    track.width = imageBitmap.width;
    track.height = imageBitmap.height;
    track.baseStart = baseStart;
    track.basePerPixel = basePerPixel;
    track.isLoaded = true;

    const totalHeight = getAllTracksHeight();
    const newScrollY = Math.max(0, Math.min(get().scrollY, totalHeight - get().canvas!.height));

    set({
      scrollY: newScrollY,
    });

    refreshMain();
  }

  // sendSyntenyRenderMessage();
}

// This comes back from the track worker once the pileup has been calculated.
function handleOrtholog(
  data: WorkerMessage["Worker"]["Ortholog"],
  get: StoreApi<GenomeViewerStore>["getState"],
  set: StoreApi<GenomeViewerStore>["setState"],
) {
  const { trackId, orthologs } = data;
  const currentState = get();

  // Only merge and update if there are new orthologs for this track
  if (Array.isArray(orthologs)) {
    const mergedOrthologs = mergeOrthologs(currentState.orthologousGroups, orthologs, trackId);

    // Update state
    set({ orthologousGroups: mergedOrthologs });

    if (
      ![...get().tracks.values()].every(
        (track) => track.trackType === getTrackTypeBySyntenyType(get().activeSyntenyType!) && track.isOrthologsLoaded,
      )
    ) {
      const newTracks = new Map(get().tracks);
      if (newTracks.has(trackId)) {
        newTracks.get(trackId)!.isOrthologsLoaded = true;
      }
      set({
        tracks: newTracks,
      });
    }

    // Keep worker state in sync with main thread
    //! This is a bottleneck
    currentState.syntenyWorker?.postMessage({
      type: "update",
      orthologousGroups: mergedOrthologs,
    });
  }

  // Early return if no alignment is active
  const family = currentState.alignedFamily;
  if (!family) return;

  // If this track is already a member, early exit
  for (let i = 0, l = family.members.length; i < l; i++) {
    if (family.members[i].trackId === trackId) return;
  }

  // Find leftmost ortholog for the current family
  let leftmostOrtholog = null;
  const targetOrthologId = family.familyId;
  const len = orthologs.length;

  for (let i = 0; i < len; i++) {
    const ortholog = orthologs[i];
    if (ortholog.orthologId !== targetOrthologId) continue;
    if (
      !leftmostOrtholog ||
      ortholog.x < leftmostOrtholog.x ||
      (ortholog.x === leftmostOrtholog.x && ortholog.y < leftmostOrtholog.y)
    ) {
      leftmostOrtholog = ortholog;
    }
  }

  if (!leftmostOrtholog) return;

  // Update family with new member
  set({
    alignedFamily: {
      ...family,
      members: [...family.members, { trackId, featureId: leftmostOrtholog.id }],
    },
  });

  alignActiveFamily();
}

// We receive which "shapes" is hovered from the track worker. It's the only one to know about the pileup.
function handleHover(
  data: MainThreadHoverMessage,
  get: StoreApi<GenomeViewerStore>["getState"],
  set: StoreApi<GenomeViewerStore>["setState"],
) {
  if (!equals(get().hoverData, data)) {
    set({ hoverData: data });
    refreshMain();
  }
}

function handleError(
  data: WorkerMessage["Worker"]["Error"],
  _get: StoreApi<GenomeViewerStore>["getState"],
  _set: StoreApi<GenomeViewerStore>["setState"],
  onError: (error: Error | FetchError) => void,
) {
  const error: Error | FetchError =
    data.name === "FetchError" ? FetchError.fromJSON(data.serializedError) : new Error(data.message);

  // As it's used in zustand, it won't throw in the application
  onError(error);
}

function handleHighlight(
  data: MainThreadHighlightMessage,
  get: StoreApi<GenomeViewerStore>["getState"],
  set: StoreApi<GenomeViewerStore>["setState"],
) {
  if (data.needToExpandPileup && get().pileupRenderingMode === PileupRenderingModeEnum.CONDENSED) {
    set({
      pileupRenderingMode: PileupRenderingModeEnum.EXPANDED,
    });
    if (data.highlightFeatureId) {
      get().tracks.get(data.trackId)?.worker.postMessage({
        type: "highlight",
        highlightFeatureId: data.highlightFeatureId,
        requiresHighlight: true,
      });
    }
  }
  refreshTracks();
}
